/**
 * Cliente de la API del motor de reportes (US-2.8, ADR 0051) — Admin Web.
 *
 * Endpoints:
 * - GET  /api/v1/admin/reports                        → catálogo filtrado por rol.
 * - GET  /api/v1/admin/reports/{id}                   → definición (filtros y columnas).
 * - POST /api/v1/admin/reports/{id}/run               → vista previa `json`, archivo (200) o 202.
 *   Con `entrega = correo` responde siempre 202 y el envío ocurre en background.
 * - GET  /api/v1/admin/reports/runs                   → historial de ejecuciones del tenant.
 * - GET  /api/v1/admin/reports/runs/{runId}           → detalle de una ejecución.
 * - GET  /api/v1/admin/reports/runs/{runId}/archivo   → artefacto generado.
 *
 * El Admin Web NO conoce reportes de antemano: todo se pinta desde la definición
 * que devuelve el backend. La descarga usa `fetch` directo porque `apiRequest`
 * normaliza el cuerpo a JSON y aquí el cuerpo es un `Blob`.
 */
import type {
  ReportCatalogResponse,
  ReportDefinition,
  ReportFormat,
  ReportRun,
  ReportRunAccepted,
  ReportRunRequest,
  ReportRunsResponse,
  ReportRunResult,
} from '@goelectric/shared-types';
import {
  ApiError,
  apiRequest,
  getAccessTokenSnapshot,
  requestSessionRefresh,
} from '@/lib/http/apiClient';
import { API_BASE_URL } from '@/lib/env';

/** Entrada de ejecución del front: `page`/`limit` solo aplican a la vista `json`. */
export interface ReportRunInput {
  filtros: Record<string, unknown>;
  /** Subconjunto de columnas declaradas; si se omite, van todas. */
  columnas?: string[];
  formato: ReportFormat;
  /** `correo` encola la generación y la entrega por email (siempre 202). */
  entrega?: 'descarga' | 'correo';
  /** Destinatarios del correo (1..10); sólo con `entrega: 'correo'`. */
  destinatarios?: string[];
  /** Asunto del correo; si falta, el backend usa el automático. */
  asunto?: string;
  page?: number;
  limit?: number;
  /** Permite cancelar la vista previa cuando llega una ejecución más nueva. */
  signal?: AbortSignal;
}

function toRunRequest(input: ReportRunInput): ReportRunRequest {
  const request: ReportRunRequest = {
    filtros: input.filtros,
    columnas: input.columnas,
    formato: input.formato,
    page: input.page ?? 1,
    limit: input.limit ?? 50,
  };
  // Se omiten cuando no aplican: un asunto vacío debe caer en el automático.
  if (input.entrega) request.entrega = input.entrega;
  if (input.destinatarios) request.destinatarios = input.destinatarios;
  if (input.asunto) request.asunto = input.asunto;
  return request;
}

/** `true` si el formato es un archivo (no la vista previa `json`). */
export function isFileFormat(formato: ReportFormat): boolean {
  return formato !== 'json';
}

/** Extrae el nombre del archivo de `Content-Disposition` (prefiere `filename*`). */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

/** Descarga un `Blob` con el nombre indicado (ancla temporal, sin tocar el DOM final). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Inicialización mínima de las peticiones que devuelven archivos o `202`. */
interface FileRequestInit {
  method: 'GET' | 'POST';
  body?: ReportRunRequest;
}

/**
 * Ejecuta la petición con el mismo esquema de sesión que `apiRequest`:
 * `Bearer` en memoria y un único refresh ante `401`. El cuerpo no se normaliza:
 * puede ser JSON (POST) o binario (GET de un artefacto).
 */
async function requestFile(
  path: string,
  init: FileRequestInit,
  isRetry = false,
): Promise<Response> {
  const headers = new Headers({ Accept: '*/*' });
  if (init.body !== undefined) headers.set('Content-Type', 'application/json');
  const token = getAccessTokenSnapshot();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: init.method,
    headers,
    credentials: 'include',
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  if (response.status === 401 && !isRetry) {
    const renewed = await requestSessionRefresh();
    if (renewed) return requestFile(path, init, true);
  }
  return response;
}

async function toApiError(response: Response): Promise<ApiError> {
  let message = `Error ${response.status}`;
  let code = 'HTTP_ERROR';
  try {
    const payload = (await response.json()) as { message?: string | string[]; error?: string };
    if (Array.isArray(payload.message)) message = payload.message.join(', ');
    else if (payload.message) message = payload.message;
    if (payload.error) code = payload.error;
  } catch {
    // El backend devolvió algo que no es JSON: se conserva el mensaje genérico.
  }
  return new ApiError({ status: response.status, code, message });
}

/**
 * Resultado de pedir un archivo: el backend puede entregarlo en el mismo
 * request (200) o aceptar la ejecución en background (202).
 */
export type ReportDownloadResult =
  | { tipo: 'archivo' }
  | { tipo: 'aceptado'; run: ReportRunAccepted };

export const reportesApi = {
  /** Catálogo de reportes visibles para el rol autenticado. */
  async list(): Promise<ReportDefinition[]> {
    const response = await apiRequest<ReportCatalogResponse>('/api/v1/admin/reports', {
      retryOnUnauthorized: true,
    });
    return response.data;
  },

  /** Definición completa de un reporte (filtros + columnas + formatos). */
  getDefinition(reportId: string): Promise<ReportDefinition> {
    return apiRequest<ReportDefinition>(
      `/api/v1/admin/reports/${encodeURIComponent(reportId)}`,
      { retryOnUnauthorized: true },
    );
  },

  /** Vista previa paginada (`formato: json`); cancelable vía `signal`. */
  run(reportId: string, input: ReportRunInput): Promise<ReportRunResult> {
    return apiRequest<ReportRunResult>(
      `/api/v1/admin/reports/${encodeURIComponent(reportId)}/run`,
      {
        method: 'POST',
        body: toRunRequest(input),
        retryOnUnauthorized: true,
        signal: input.signal,
      },
    );
  },

  /**
   * Ejecuta el reporte en `csv`/`xlsx`/`pdf`.
   *
   * - 200: dispara la descarga del archivo y devuelve `{ tipo: 'archivo' }`.
   * - 202: la ejecución quedó en background; devuelve el run aceptado para que
   *   la pantalla lo registre y refresque el historial.
   *
   * El nombre lo manda el backend en `Content-Disposition`; si falta, se usa
   * `<id>.<formato>` como último recurso.
   */
  async download(reportId: string, input: ReportRunInput): Promise<ReportDownloadResult> {
    const response = await requestFile(
      `/api/v1/admin/reports/${encodeURIComponent(reportId)}/run`,
      { method: 'POST', body: toRunRequest(input) },
    );
    if (response.status === 202) {
      const run = (await response.json()) as ReportRunAccepted;
      return { tipo: 'aceptado', run };
    }
    if (!response.ok) {
      throw await toApiError(response);
    }
    const blob = await response.blob();
    const filename =
      filenameFromDisposition(response.headers.get('content-disposition')) ??
      `${reportId}.${input.formato}`;
    saveBlob(blob, filename);
    return { tipo: 'archivo' };
  },

  /**
   * Encola el reporte para enviarlo por correo. El contrato garantiza 202 con
   * el run aceptado sin importar el volumen; el archivo queda disponible en
   * `/runs/{runId}/archivo` cuando la generación termine.
   */
  async sendByEmail(reportId: string, input: ReportRunInput): Promise<ReportRunAccepted> {
    const response = await requestFile(
      `/api/v1/admin/reports/${encodeURIComponent(reportId)}/run`,
      { method: 'POST', body: toRunRequest({ ...input, entrega: 'correo' }) },
    );
    if (!response.ok) {
      throw await toApiError(response);
    }
    return (await response.json()) as ReportRunAccepted;
  },

  /** Historial paginado de ejecuciones del tenant (más recientes primero). */
  listRuns(offset: number, limit: number): Promise<ReportRunsResponse> {
    const query = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    return apiRequest<ReportRunsResponse>(`/api/v1/admin/reports/runs?${query}`, {
      retryOnUnauthorized: true,
    });
  },

  /** Detalle de una ejecución (estado, total, truncado y error). */
  getRun(runId: string): Promise<ReportRun> {
    return apiRequest<ReportRun>(`/api/v1/admin/reports/runs/${encodeURIComponent(runId)}`, {
      retryOnUnauthorized: true,
    });
  },

  /**
   * Descarga el artefacto de una ejecución `LISTO`. Los errores del contrato
   * (409 sin artefacto, 410 expirado, 404 ajeno/inexistente) se propagan como
   * `ApiError` para que la pantalla elija el mensaje.
   */
  async downloadArtifact(run: ReportRun): Promise<void> {
    const response = await requestFile(
      `/api/v1/admin/reports/runs/${encodeURIComponent(run.id)}/archivo`,
      { method: 'GET' },
    );
    if (!response.ok) {
      throw await toApiError(response);
    }
    const blob = await response.blob();
    const filename =
      filenameFromDisposition(response.headers.get('content-disposition')) ??
      `${run.reporteId}.${run.formato}`;
    saveBlob(blob, filename);
  },
};
